import { Fragment, cloneElement, useEffect, useState } from 'react';

import { useApiCall } from 'redux/utils/api-hook';

import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';

import {
  DialogContentContainer,
  DialogMainContent,
} from '../dialog/dialogStyledComponents';

import { AssignedWorkflowPhaseDto } from '@act/shared/data-transfer-objects';
import { workflowsRequests } from 'redux/requests/workflows.request';
import { DialogFooter } from '../dialog/dialog-footer';
import { DialogHeader } from '../dialog/dialog-header';
import WorkflowCompletionDialog from './workflow-completion-dialog';
import {
  ProgressContainerWithSpinner,
  SECTION_TYPES,
} from './workflow-dialog-shared.constants';
import AttachWorkflowToEventDialog from './workflow-event-dialog';

interface LearnMoreDialogProps {
  open: boolean;
  handleClose: () => void;
  assignedWorkflowPhase: AssignedWorkflowPhaseDto;
}

const LearnMoreDialog = ({
  open,
  handleClose,
  assignedWorkflowPhase,
}: LearnMoreDialogProps) => {
  const [sections, isLoading, getSectionsByPhaseId] = useApiCall(
    workflowsRequests.getSectionsByPhaseId
  );

  const [dialogSections, setDialogSections] = useState([]);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const handleCompleteNow = () => {
    setWorkflowOpen(true);
  };

  const handleCloseWorkflow = (workflowFinished?: boolean) => {
    setWorkflowOpen(false);
    if (workflowFinished) {
      handleClose();
    }
  };

  useEffect(() => {
    getSectionsByPhaseId({
      params: {
        phaseId: assignedWorkflowPhase.workflowPhase?.id,
        attachContent: 'true',
      },
    });
  }, [assignedWorkflowPhase.workflowPhase.id]);

  useEffect(() => {
    if (sections?.length) {
      setDialogSections(
        sections.filter(
          section =>
            section.usableIn === 'learnMore' || section.usableIn === 'both'
        )
      );
    }
  }, [sections]);

  return (
    <>
      <Dialog
        maxWidth="xl"
        open={open}
        onClose={handleClose}
        role="<dialog>"
        aria-label="Learn more dialog"
      >
        <DialogContentContainer>
          <DialogHeader
            postfixAction={{
              title: 'close',
              callback: handleClose,
              icon: <CloseIcon />,
            }}
          >
            Learn More — {assignedWorkflowPhase.workflowPhase?.title}
          </DialogHeader>
          <DialogMainContent>
            {isLoading && <ProgressContainerWithSpinner />}
            {!isLoading &&
              dialogSections.map((section, index) => {
                return (
                  <Fragment
                    key={`dialog-section-section-${section.type}-${index}`}
                  >
                    {cloneElement(SECTION_TYPES[section.type], { section })}
                  </Fragment>
                );
              })}
          </DialogMainContent>
          <DialogFooter
            actions={[
              {
                text: 'Complete now',
                callback: handleCompleteNow,
              },
              {
                text: 'Schedule now',
                isPrimary: true,
                callback: () => setScheduleOpen(true),
              },
            ]}
          />
        </DialogContentContainer>
      </Dialog>
      {workflowOpen && (
        <WorkflowCompletionDialog
          open={workflowOpen}
          assignedPhaseId={assignedWorkflowPhase.id}
          phase={assignedWorkflowPhase.workflowPhase}
          responseId={assignedWorkflowPhase.surveyResponseId}
          handleClose={handleCloseWorkflow}
        />
      )}
      {scheduleOpen && (
        <AttachWorkflowToEventDialog
          open={scheduleOpen}
          handleClose={() => setScheduleOpen(false)}
          userId={assignedWorkflowPhase.userId}
          description={assignedWorkflowPhase?.workflowPhase?.description}
          assignedWorkflowPhaseId={assignedWorkflowPhase.id}
        />
      )}
    </>
  );
};

export default LearnMoreDialog;
